import { Currency, currencyEquals } from '@uniswap/sdk-core'
import { Pool } from '@uniswap/v3-sdk'
import { ButtonPrimary } from 'components/Button'
import { BlueCard, LightCard, OutlineCard, YellowCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import { PositionPreview } from 'components/PositionPreview'
import RangeSelector from 'components/RangeSelector'
import RateToggle from 'components/RateToggle'
import { RowBetween } from 'components/Row'
import { DynamicSection, StepReview, StyledInput } from 'pages/AddLiquidity/styled'
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo } from 'react'
import { AlertCircle, AlertTriangle } from 'react-feather'
import { useRangeHopCallbacks } from 'state/mint/v3/hooks'
import { ThemeContext } from 'styled-components'
import { TYPE } from 'theme'
import { formatPriceString } from 'utils/formatTokenAmount'

import ChartRange from './ChartRange'
import { PresetPriceRange } from 'pages/AddLiquidity'

interface Step1Props {
  activeStep: number
  noLiquidity: boolean | undefined
  currencyA?: any
  currencyB?: any

  feeAmount?: number
  currencyIdA?: string
  currencyIdB?: string
  startPriceTypedValue: string
  onStartPriceInput: (val: string) => void
  price?: any
  invertPrice: boolean
  onLeftRangeInput: (val: string) => void
  onRightRangeInput: (val: string) => void
  history: any
  hasExistingPosition: boolean
  existingPosition?: any
  outOfRange: boolean
  invalidPool: boolean
  invalidRange: boolean
  step1Validate: boolean
  handleStep: (step: number) => void
  priceLower?: any
  priceUpper?: any

  setPresetPriceRange: (preset: PresetPriceRange) => void
  presetPriceRange: PresetPriceRange

  tickLower: number | undefined
  tickUpper: number | undefined
  pool: Pool | null | undefined
  baseCurrency: Currency | null | undefined
  triggerInfiniteRange: boolean
  setTriggerInfiniteRange: Dispatch<SetStateAction<boolean>>
  setBaseCurrency: Dispatch<SetStateAction<Currency | null | undefined>>
}

const Step1: React.FC<Step1Props> = ({
  activeStep,
  noLiquidity,
  currencyA,
  currencyB,

  feeAmount,
  currencyIdA,
  currencyIdB,
  startPriceTypedValue,
  onStartPriceInput,
  price,
  invertPrice,
  onLeftRangeInput,
  onRightRangeInput,
  history,
  hasExistingPosition,
  existingPosition,
  outOfRange,
  invalidPool,
  invalidRange,
  step1Validate,
  handleStep,
  priceLower,
  priceUpper,
  presetPriceRange,
  setPresetPriceRange,
  tickLower,
  tickUpper,
  pool,
  baseCurrency,
  setBaseCurrency,
  triggerInfiniteRange,
  setTriggerInfiniteRange,
}) => {
  const theme = useContext(ThemeContext)

  //Hot fix
  const [invertedFromAPI, setInvertedFromAPI] = React.useState(false)

  const quoteCurrency = useMemo(
    () =>
      currencyA && currencyB && baseCurrency
        ? currencyEquals(baseCurrency, currencyA)
          ? currencyB
          : currencyA
        : undefined,
    [currencyA, currencyB, baseCurrency]
  )
  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper } = useRangeHopCallbacks(
    baseCurrency ?? undefined,
    quoteCurrency ?? undefined,
    feeAmount,
    tickLower,
    tickUpper,
    pool
  )

  // url params are the source truth, so we have to do this
  useEffect(() => {
    setBaseCurrency(currencyA)
    return () => {
      setBaseCurrency(undefined)
    }
  }, [currencyA, currencyB, setBaseCurrency])

  return (
    <>
      {activeStep === 1 && (
        <>
          {noLiquidity && (
            <DynamicSection disabled={!currencyA || !currencyB}>
              <AutoColumn gap="md">
                <RowBetween>
                  <TYPE.label>Set Starting Price</TYPE.label>
                  {baseCurrency && quoteCurrency ? (
                    <RateToggle
                      currencyA={baseCurrency}
                      currencyB={quoteCurrency}
                      handleRateToggle={() => {
                        onLeftRangeInput('')
                        onRightRangeInput('')
                        history.push(
                          `/add/${currencyIdB as string}/${currencyIdA as string}${feeAmount ? '/' + feeAmount : ''}`
                        )
                      }}
                    />
                  ) : null}
                </RowBetween>

                <OutlineCard padding="12px">
                  <StyledInput
                    className="start-price-input"
                    value={startPriceTypedValue}
                    onUserInput={onStartPriceInput}
                  />
                </OutlineCard>
                <RowBetween style={{ backgroundColor: theme.bg1, padding: '12px', borderRadius: '12px' }}>
                  <TYPE.main textAlign="center">Current {baseCurrency?.symbol} Price:</TYPE.main>
                  <TYPE.main textAlign="center">
                    {price ? (
                      <TYPE.main textAlign="center">
                        {invertPrice ? price?.invert()?.toSignificant(8) : price?.toSignificant(8)}{' '}
                        {quoteCurrency?.symbol}
                      </TYPE.main>
                    ) : (
                      '-'
                    )}
                  </TYPE.main>
                </RowBetween>
                <BlueCard
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: ' 1.5rem 1.25rem',
                  }}
                >
                  <AlertCircle color={theme.text1} size={32} style={{ marginBottom: '12px', opacity: 0.8 }} />
                  <TYPE.body
                    fontSize={14}
                    style={{ marginBottom: 8, fontWeight: 500, opacity: 0.8 }}
                    textAlign="center"
                  >
                    You are the first liquidity provider for this Hyperswap V3 pool.
                  </TYPE.body>
                </BlueCard>
              </AutoColumn>
            </DynamicSection>
          )}

          {hasExistingPosition && existingPosition ? (
            <PositionPreview position={existingPosition} title={'Selected Range'} inRange={!outOfRange} />
          ) : (
            <>
              <DynamicSection gap="md" disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue)}>
                <RowBetween>
                  <TYPE.label>Set Price Range</TYPE.label>
                  {baseCurrency && quoteCurrency ? (
                    <RateToggle
                      currencyA={baseCurrency}
                      currencyB={quoteCurrency}
                      handleRateToggle={() => {
                        onLeftRangeInput('')
                        onRightRangeInput('')
                        setInvertedFromAPI(!invertedFromAPI)
                        history.push(
                          `/add/${currencyIdB as string}/${currencyIdA as string}${feeAmount ? '/' + feeAmount : ''}`
                        )
                      }}
                    />
                  ) : null}
                </RowBetween>
                <TYPE.main fontSize={14} fontWeight={400} style={{ marginBottom: '.5rem', lineHeight: '125%' }}>
                  Your liquidity will only earn fees when the market price of the pair is within your range.{' '}
                </TYPE.main>

                <RangeSelector
                  priceLower={priceLower}
                  priceUpper={priceUpper}
                  getDecrementLower={getDecrementLower}
                  getIncrementLower={getIncrementLower}
                  getDecrementUpper={getDecrementUpper}
                  getIncrementUpper={getIncrementUpper}
                  onLeftRangeInput={onLeftRangeInput}
                  onRightRangeInput={onRightRangeInput}
                  currencyA={baseCurrency}
                  currencyB={quoteCurrency}
                  feeAmount={feeAmount}
                  setPresetPriceRange={setPresetPriceRange}
                  presetPriceRange={presetPriceRange}
                  price={invertPrice ? price?.invert()?.toSignificant(3) : price?.toSignificant(3)}
                  triggerInfiniteRange={triggerInfiniteRange}
                  setTriggerInfiniteRange={setTriggerInfiniteRange}
                />

                {price && baseCurrency && quoteCurrency && !noLiquidity && (
                  <LightCard style={{ padding: '12px', overflow: 'auto' }}>
                    <AutoColumn gap="4px">
                      <TYPE.main fontWeight={500} textAlign="center" fontSize={12}>
                        Current Price
                      </TYPE.main>
                      <TYPE.body fontWeight={500} textAlign="center" fontSize={20} style={{ textAlign: 'center' }}>
                        {invertPrice ? price.invert().toSignificant(3) : price.toSignificant(3)}{' '}
                      </TYPE.body>
                      <TYPE.main fontWeight={500} textAlign="center" fontSize={12}>
                        {quoteCurrency?.symbol} {' / '}
                        {baseCurrency.symbol}
                      </TYPE.main>
                    </AutoColumn>
                  </LightCard>
                )}

                {outOfRange ? (
                  <YellowCard padding="8px 12px" borderRadius="12px" maxWidth={'500px'}>
                    <RowBetween>
                      <AlertTriangle stroke={theme.yellow3} size="16px" />
                      <TYPE.yellow ml="12px" fontSize="12px">
                        Your position will not earn fees or be used in trades until the market price moves into your
                        range.
                      </TYPE.yellow>
                    </RowBetween>
                  </YellowCard>
                ) : null}

                {invalidRange ? (
                  <YellowCard padding="8px 12px" borderRadius="12px" maxWidth={'500px'}>
                    <RowBetween>
                      <AlertTriangle stroke={theme.yellow3} size="16px" />
                      <TYPE.yellow ml="12px" fontSize="12px">
                        Invalid range selected. The min price must be lower than the max price.
                      </TYPE.yellow>
                    </RowBetween>
                  </YellowCard>
                ) : null}

                <ChartRange
                  currencyA={baseCurrency as Currency}
                  currencyB={quoteCurrency as Currency}
                  currencyIdA={currencyIdA}
                  currencyIdB={currencyIdB}
                  tokenASymbol={currencyA?.symbol}
                  tokenBSymbol={currencyB?.symbol}
                  feeAmount={feeAmount}
                  price={invertPrice ? price?.invert()?.toSignificant(3) : price?.toSignificant(3)}
                  priceLower={priceLower}
                  priceUpper={priceUpper}
                  onLeftRangeInput={onLeftRangeInput}
                  onRightRangeInput={onRightRangeInput}
                  presetPriceRange={presetPriceRange}
                  invertedFromAPI={invertedFromAPI}
                  setInvertedFromAPI={setInvertedFromAPI}
                />
                {/*   <ChartLiquidity
                  currencyA={baseCurrency as Currency}
                  currencyB={quoteCurrency as Currency}
                  currencyIdA={currencyIdA}
                  currencyIdB={currencyIdB}
                  tokenASymbol={currencyA?.symbol}
                  tokenBSymbol={currencyB?.symbol}
                  feeAmount={feeAmount}
                  invertedFromAPI={invertedFromAPI}
                /> */}
              </DynamicSection>
            </>
          )}

          <ButtonPrimary
            disabled={!step1Validate}
            onClick={() => {
              handleStep(2)
            }}
          >
            Continue
          </ButtonPrimary>
        </>
      )}

      {activeStep > 1 && step1Validate && (
        <StepReview>
          <span className="title">Step 2</span>
          <div className="value">
            {presetPriceRange === 'maxRange' ? (
              'Max price range'
            ) : (
              <>
                {' '}
                {formatPriceString(priceLower?.toSignificant(5))} - {formatPriceString(priceUpper?.toSignificant(5))}
              </>
            )}
          </div>
          <button onClick={() => handleStep(1)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
              <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
              <path d="M16 5l3 3" />
            </svg>
            Edit
          </button>
        </StepReview>
      )}
    </>
  )
}
export default React.memo(Step1)
